import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  Type,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormField } from '../../../dto/util/form-field';
import { ListasService } from '../../../services/listas.service';
import { MunicipioDto } from '../../../dto/expediente/municipio.dto';
import { PaisDto } from '../../../dto/expediente/pais.dto';
import { DepartmentoDto } from '../../../dto/expediente/departmento.dto';
import { TipoDocumentoDto } from '../../../dto/expediente/tipo-documento-dto.dto';
import { ClaseSolicitudDto } from '../../../dto/expediente/clase-solicitud-dto.dto';
import { AsuntoTramiteDto } from '../../../dto/expediente/asuntoTramite.dto';
import { Constants } from '../../../constants/constants';
import { TranslateService } from "@ngx-translate/core";

// Definir el tipo de función para el constructor del Tooltip de Bootstrap
type BootstrapTooltipConstructor = new (
  element: HTMLElement,
  options?: unknown
) => void;

// Definir la interfaz para Bootstrap
interface Bootstrap {
  Tooltip: BootstrapTooltipConstructor;
}

// Declarar bootstrap con el tipo Bootstrap
declare const bootstrap: Bootstrap;

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class FormComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() title = '';

  @Input() titleModalVisibility = false;
  @Input() titleModal = '';
  @Input() titleSizeModal = '';
  @Input() titleContentModalText = '';
  @Input() tooltipVisibility = false;
  @Input() tooltipMessage = '';

  @Input() fields: FormField[] = [];
  @Input() group!: FormGroup;
  @Output() submitAction = new EventEmitter<FormGroup>();
  @Output() changeAction = new EventEmitter<FormGroup>();
  @Output() changedControl = new EventEmitter<{
    controlName?: string;
    controlValue: unknown;
    form: FormGroup;
  }>();
  @Input() withSubmitButton = true;
  @Input() withCamposObligatorios = true;
  @Input() sumbitButtonText = '';
  @Input() dynamicComponent!: Type<unknown>;
  @Input() idEntidad: number = 0;
  @Input() filtrarTiposDocumento = false;
  @Input() dependsOnWithValueField?: string;
  @Input() edadMinima: number | undefined;

  @Input() alwaysEnableSubmit: boolean = false;
  @Input() tresColumnas = false;
  personTypeId!: string;

  form!: FormGroup;

  formattedCountries: PaisDto[] = [];

  maxDate: Date;

  showPassword: { [key: string]: boolean } = {};

  // Multilenguaje
  comunText: any = {};

  previewImages: { [key: string]: string | ArrayBuffer | null } = {};
  constructor(
    private fb: FormBuilder,
    private listasService: ListasService,
    private translate: TranslateService
  ) {
    this.maxDate = new Date();
  }

  ngOnInit(): void {
    this.translate.stream('comun-label').forEach(text => {
      for (const key of Object.keys(text)) {
        this.comunText[key] = text[key];
      }
    });
    if ((this.sumbitButtonText ?? '').trim().length == 0) {
      this.sumbitButtonText = this.comunText['continuar'];
    }
    this.form = this.fb.group({});
    this.fields.forEach(field => {
      if (field.name.startsWith('typeOfDocument')) {
        this.personTypeId = field.personTypeId ?? '0';
      }
      // console.log(field)
      const control = this.fb.control(
        { value: field.value, disabled: field.disabled || false },
        this.getValidators(field)
      );
      this.form.addControl(field.name, control);
      this.form.get(field.name)?.updateValueAndValidity();
    });

    this.form.valueChanges.subscribe(() => {
      this.updateFieldVisibility();
    });

    this.updateFieldVisibility();

    this.changedControl.emit({ controlValue: '', form: this.form });
    this.changeAction.emit(this.form);

    this.setupDynamicValidation();

    // Inicializar el select de países y tipos de documento
    this.getDocumentTypes(this.personTypeId);
    this.getRequestClass();
    this.getAsuntoTramite();

    for (const control in this.form.controls) {
      if (control.startsWith('country')) {
        const idUnico = control.replace('country', '');
        // Escuchar cambios en el select de país para cargar departamentos
        this.form.get(control)?.valueChanges.subscribe(countryId => {
          this.form.get('departament' + idUnico)?.reset();
          this.form.get('municipality' + idUnico)?.reset();
          if (countryId) {
            this.getDepartments(countryId, idUnico);
          }
        });
      }

      if (control.startsWith('departament')) {
        const idUnico = control.replace('departament', '');
        // Escuchar cambios en el select de departamento para cargar municipios
        this.form.get(control)?.valueChanges.subscribe(departmentId => {
          if (departmentId) {
            this.getMunicipalities(departmentId, idUnico);
          }
          this.form.get('municipality' + idUnico)?.reset();
        });
      }

      if (control.startsWith('tipoPersona')) {
        this.form.get(control)?.valueChanges.subscribe(tipoPersonaId => {
          if (tipoPersonaId) {
            this.updateTypesOfDocument();
          }
        });
      }
    }

    this.fields.forEach(field => {
      const modalId = field.titleModal
        ? this.generateModalId(field.titleModal)
        : 'defaultModalId';
      this.closeModal(modalId);
    });
    this.getCountries();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const campos = changes['fields'];
    if (campos) {
      for (const f of campos.currentValue) {
        if (campos.previousValue) {
          const oldField = campos.previousValue?.find(
            (c: FormField) => c.name === f.name
          ) as FormField;
          const field = f as FormField;
          if (oldField?.label !== field.label) {
            field.value = oldField?.value;
            field.disabled = oldField?.disabled;
            field.options = oldField?.options?.map(option => {
              return {
                Id: option.Id,
                Nombre:
                  field.options?.find(o => o.Id === option.Id)?.Nombre ||
                  option.Nombre,
                selected:
                  field.options?.find(o => o.Id === option.Id)?.selected ||
                  option.selected,
              };
            });
          }
          if (field.type === 'select') {
            // Aseguramos que el campo Id siempre sea un número
            const optionsFormatted = (field.options || []).map(option => ({
              Id: option.Id,
              Nombre: option.Nombre,
            }));
            this.updateSelectOptions(field.name, optionsFormatted);
          }
          if (this.form?.get(field.name)?.value != field.value) {
            if (field.type == 'select') {
              setTimeout(() => {
                this.form?.get(field.name)?.setValue(field.value);
                this.form?.get(field.name)?.updateValueAndValidity();
              }, 2000);
            }
            this.form?.get(field.name)?.setValue(field.value);
            this.form?.get(field.name)?.updateValueAndValidity();
          }
          // Se actualizan disabled si se requiere
          this.form?.get(field.name)?.[field.disabled ? 'disable' : 'enable']();
        }
      }
      this.changedControl.emit({ controlValue: '', form: this.form });
      this.changeAction.emit(this.form);
    }
  }

  ngAfterViewInit(): void {
    // Selecciona todos los elementos que tengan el atributo data-bs-toggle="tooltip"
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]'
    );
    // Inicializa cada tooltip sin asignarlo a una variable
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      new bootstrap.Tooltip(tooltipTriggerEl as HTMLElement);
    });
  }

  getValidators(field: FormField): ValidatorFn[] {
    const validators = [];
    if (field.required) {
      validators.push(Validators.required);
    }
    if (field.validation) {
      validators.push(...field.validation);
    }
    if (field.validation?.length === 0) {
      if (
        field.type == 'text' ||
        field.type == 'textarea' ||
        field.type == 'number' ||
        field.type == 'tel'
      ) {
        validators.push(Validators.pattern(Constants.REGEX_WITHOUT_ESPACES));
      }
    }
    return validators;
  }

  setupDynamicValidation(): void {
    // Obtener todos los controles que empiezan con 'numberOfDocument'
    const documentFieldControls = Object.keys(this.form.controls)
      .filter(controlName => controlName.startsWith('numberOfDocument'))
      .map(controlName => this.form.get(controlName));

    // Obtener todos los controles que empiezan con 'typeOfDocument'
    const typeOfDocumentControls = Object.keys(this.form.controls)
      .filter(controlName => controlName.startsWith('typeOfDocument'))
      .map(controlName => this.form.get(controlName));

    // Obtener todos los controles que empiezan con 'phone'
    const phoneControls = Object.keys(this.form.controls)
      .filter(controlName => controlName.startsWith('phone'))
      .map(controlName => this.form.get(controlName));

    // Obtener todos los controles que empiezan con 'fechaNacimiento'
    const fechaNacimientoControls = Object.keys(this.form.controls)
      .filter(controlName => controlName.startsWith('fechaNacimiento'))
      .map(controlName => this.form.get(controlName));

    // Validaciones dinámicas para los controles de tipo de documento
    typeOfDocumentControls.forEach(typeOfDocumentControl => {
      if (typeOfDocumentControl) {
        typeOfDocumentControl.valueChanges.subscribe(selectedValue => {
          documentFieldControls.forEach(documentFieldControl => {
            if (documentFieldControl) {
              documentFieldControl.clearValidators();
              /*validacion CC y TI*/
              if (selectedValue === 1
                || selectedValue === 2
                || selectedValue === 5) {
                documentFieldControl.setValidators([
                  Validators.required,
                  Validators.minLength(4),
                  Validators.maxLength(12),
                  Validators.pattern(/^\d+$/), // Solo números
                ]);
              } else if (selectedValue === 4) {
                /*validacion pasaporte */
                documentFieldControl.setValidators([
                  Validators.required,
                  Validators.minLength(4),
                  Validators.maxLength(8),
                  Validators.pattern(/^[a-zA-Z0-9]+$/), // Letras y números
                ]);
              } else if (selectedValue === 3) {
                /*validacion NIT */
                documentFieldControl.setValidators([
                  Validators.required,
                  Validators.maxLength(9),
                  Validators.pattern(/^\d+$/), // solo números
                ]);
              }

              documentFieldControl.updateValueAndValidity(); // Actualizar el estado de validación
            }
          });
        });
      }
    });

    // Validaciones para los campos de teléfono
    phoneControls.forEach(phoneControl => {
      if (phoneControl) {
        phoneControl.setValidators([
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern(/^[\d]+$/), // Solo números
        ]);
        phoneControl.updateValueAndValidity();
      }
    });

    // Validaciones para los campos fecha nacimiento
    fechaNacimientoControls.forEach(fechaNacimientoControl => {
      if (fechaNacimientoControl) {
        if (this.edadMinima !== undefined) {
          fechaNacimientoControl.addValidators([
            this.edadMinimaValidator(this.edadMinima),
          ]);
          fechaNacimientoControl.updateValueAndValidity();
        } else {
          fechaNacimientoControl.addValidators([
            this.edadMinimaValidator(Constants.ANIOS_REQUERIDOS),
          ]);
          fechaNacimientoControl.updateValueAndValidity();
        }
      }
    });
  }

  // transformToUppercase(fieldName: string, tipo: string): void {
  //   const control = this.form.get(fieldName);
  //   if (control) {
  //     const uppercasedValue = control.value?.toUpperCase();
  //     control.setValue(uppercasedValue, { emitEvent: false });
  //   } else {
  //   }
  // }

  // Función que transforma los campos de correo en mayúsculas, con las validaciones que
  // evitan re-render en cada pulsación, duplicaciones de eventos o transformaciones y salto de cursor
  transformToUppercase(fieldName: string, tipo: string) {
    const control = this.form.get(fieldName);
    if (!control) return;

    const value = control.value ?? '';
    const newValue = value.toUpperCase();

    // Evitar actualización si no hay cambios
    if (value === newValue) return;

    control.setValue(newValue, { emitEvent: false });
  }
  getErrorMessage(field: FormField): string {
    const control = this.form.get(field.name);
    const typeOfDocument = this.form.get('typeOfDocument')?.value;
    if (control?.errors && field.errorMessages) {
      for (const key in control.errors) {
        if ((typeOfDocument?.toString() === '1' || typeOfDocument?.toString() === '5'
          || typeOfDocument?.toString() === '2') && field.name === 'numberOfDocument') {
          if (key === 'minlength') return this.comunText['min-4'];
          if (key === 'maxlength') return this.comunText['max-12'];
          if (key === 'pattern')
            return this.comunText['solo-numeros'];
        }
        if (typeOfDocument?.toString() === '4' && field.name === 'numberOfDocument') {
          if (key === 'minlength') return this.comunText['min-4'];
          if (key === 'maxlength') return this.comunText['max-8'];
          if (key === 'pattern')
            return this.comunText['letras-y-numeros'];
        }
        if (typeOfDocument?.toString() === '3' && field.name === 'numberOfDocument') {
          if (key === 'maxlength') return this.comunText['max-9'];
          if (key === 'pattern')
            return this.comunText['solo-numeros'];
        }
        if (key === 'pattern') {
          if (control.errors['pattern']['requiredPattern']) {
            return field.errorMessages['pattern'];
          } else {
            return this.comunText['espacios'];
          }
        }
        if (key === 'maxWords') {
          return field.errorMessages['maxWords']
            ? `${field.errorMessages['maxWords']}. Llevas ${control.errors['maxWords'].actualWords} palabras de un máximo de ${control.errors['maxWords'].requiredWords}.`
            : `El texto no puede superar ${control.errors['maxWords'].requiredWords} palabras. Llevas ${control.errors['maxWords'].actualWords}.`;
        }
        if (field.errorMessages[key]) {
          return field.errorMessages[key];
        }
        if (key === 'is-invalid') {
          return field.errorMessages['invalidField'];
        }
        if (key === 'invalid-date-range') {
          return field.errorMessages['invalidRange'];
        }
      }
    } else {
      if (control?.errors) {
        for (const key in control.errors) {
          if (key === 'pattern') {
            return this.comunText['espacios'];
          }
        }
      }
    }
    return '';
  }

  updateFieldVisibility() {
    this.fields.forEach(field => {
      let country;
      let visibilityUbicacions = true;
      if (field.name.startsWith('departament')) {
        country = this.form.get(
          'country' + field.name.replace('departament', '')
        );
      } else if (field.name.startsWith('municipality')) {
        country = this.form.get(
          'country' + field.name.replace('municipality', '')
        );
      }
      if (country && !field.dependsOn) {
        const countryValue = this.formattedCountries.find(
          c => c.Id === country.value
        );
        field.visible = countryValue?.Codigo === 'CO';
        return;
      } else if (country && field.dependsOn) {
        const countryValue = this.formattedCountries.find(
          c => c.Id === country.value
        );
        visibilityUbicacions = countryValue?.Codigo === 'CO';
      }
      if (field.dependsOn) {
        const dependentValue = this.form.get(field.dependsOn)?.value;
        field.visible = visibilityUbicacions &&
          (field.visibilityCondition?.(dependentValue, this.form) ??
            field.visible);
      } else {
        field.visible = visibilityUbicacions && (field.visibilityCondition
          ? field.visibilityCondition(
            this.form.get(field.name)?.value,
            this.form
          )
          : field.visible);
      }
    });
  }

  updateTypesOfDocument() {
    this.fields.forEach(field => {
      if (field.typeOfDocumentsDepends) {
        const valor = '' + this.form.get(field.name)?.value;
        field.typeOfDocumentsDepends.forEach(fieldControl => {
          this.form.get(fieldControl)?.reset();
          this.getDocumentTypes(valor, fieldControl);
        });
      }
    });
  }

  /* metodos listas */

  getCountries(): void {
    this.listasService.getCountryList().subscribe((countries: PaisDto[]) => {
      this.formattedCountries = countries;
      const fieldsToUpdate = this.fields
        .filter(field => field.name.startsWith('country'))
        .map(field => field.name);
      let values = fieldsToUpdate.map(
        f => this.fields.find(campo => campo.name === f)?.value
      );
      this.updateSelectOptions(fieldsToUpdate, this.formattedCountries);
      for (let i = 0; i < fieldsToUpdate.length; i++) {
        if (values[i] !== undefined && values[i] !== null) {
          this.form.get(fieldsToUpdate[i])?.setValue(values[i]);
          this.form.get(fieldsToUpdate[i])?.updateValueAndValidity();
        }
      }
    });
  }

  getDepartments(idCountry: number, idUnico: string): void {
    this.listasService
      .getDepartmentList(idCountry)
      .subscribe((departments: DepartmentoDto[]) => {
        const formattedDepartments = departments.map(department => ({
          Id: department.Id,
          Nombre: department.Nombre,
        }));
        const fieldsToUpdate = this.fields
          .filter(field => field.name === 'departament' + idUnico)
          .map(field => field.name);
        this.updateSelectOptions(fieldsToUpdate, formattedDepartments);
        const selectedCountry = this.formattedCountries.find(
          country => country.Id === idCountry
        );
        if (selectedCountry?.Codigo !== 'CO') {
          this.form
            .get('departament' + idUnico)
            ?.setValue(formattedDepartments[0]?.Id);
          this.form.get('departament' + idUnico)?.updateValueAndValidity();
          this.fields
            .filter(field => field.name === 'departament' + idUnico)
            .forEach(field => {
              field.visible = false;
            });
        } else {
          this.fields
            .filter(field => field.name === 'departament' + idUnico)
            .forEach(field => {
              if (field.dependsOn) {
                const dependentValue = this.form.get(field.dependsOn)?.value;
                field.visible = field.visibilityCondition?.(dependentValue, this.form) ??
                  field.visible;
              } else {
                field.visible = field.visibilityCondition
                  ? field.visibilityCondition(
                    this.form.get(field.name)?.value,
                    this.form
                  )
                  : field.visible;
              }
            });
        }
      });
  }

  getMunicipalities(idDepartment: number, idUnico: string): void {
    if (typeof idDepartment === 'string') {
      return;
    }
    this.listasService
      .getMunicipalityList(idDepartment)
      .subscribe((municipalities: MunicipioDto[]) => {
        const formattedMunicipalities = municipalities.map(municipalities => ({
          Id: municipalities.Id,
          Nombre: municipalities.Nombre,
        }));
        const fieldsToUpdate = this.fields
          .filter(field => field.name === 'municipality' + idUnico)
          .map(field => field.name);
        this.updateSelectOptions(fieldsToUpdate, formattedMunicipalities);
        const selectedCountry = this.formattedCountries.find(
          country => country.Id === this.form.get('country' + idUnico)?.value
        );
        if (selectedCountry?.Codigo !== 'CO') {
          this.form
            .get('municipality' + idUnico)
            ?.setValue(formattedMunicipalities[0]?.Id);
          this.form.get('municipality' + idUnico)?.updateValueAndValidity();
          this.fields
            .filter(field => field.name === 'municipality' + idUnico)
            .forEach(field => {
              /** Se limpia opciones de municipio */
              field.options = [];
              field.visible = false;
            });
        } else {
          this.fields
            .filter(field => field.name === 'municipality' + idUnico)
            .forEach(field => {
              field.visible = true;
            });
        }
      });
  }

  getDocumentTypes(
    personTypeId: string,
    fieldsReload: string | undefined = undefined
  ): void {
    if (personTypeId != '3') {
      this.listasService
        .getDocumentTypeListByPersonType(personTypeId, this.idEntidad)
        .subscribe((documentTypes: TipoDocumentoDto[]) => {
          let formattedDocumentTypes = documentTypes.map(documentType => ({
            Id: documentType.Id,
            TipoDocumento: documentType.Descripcion,
          }));
          if (this.filtrarTiposDocumento) {
            formattedDocumentTypes = formattedDocumentTypes.filter(field => field.Id !== 6 && field.Id !== 7)
          }
          const fieldsToUpdate = fieldsReload
            ? fieldsReload
            : this.fields
              .filter(field => field.name.startsWith('typeOfDocument'))
              .map(field => field.name);
          this.updateSelectOptions(fieldsToUpdate, formattedDocumentTypes);
        });
    } else {
      this.listasService
        .getDocumentTypeListByPersonType('1', this.idEntidad)
        .subscribe((documentTypes: TipoDocumentoDto[]) => {
          const filteredDocumentTypes = documentTypes.filter(
            documentType => documentType.Id !== 5
          );
          const formattedDocumentTypes = filteredDocumentTypes.map(
            documentType => ({
              Id: documentType.Id,
              TipoDocumento: documentType.Descripcion,
            })
          );

          // Determinar los campos a actualizar
          const fieldsToUpdate = fieldsReload
            ? fieldsReload
            : (() => {
              const rlFields = this.fields.filter(field => field.name.startsWith('typeOfDocumentRl'));
              return rlFields.length > 0
                ? rlFields.map(field => field.name)
                : this.fields
                  .filter(field => field.name.startsWith('typeOfDocument'))
                  .map(field => field.name);
            })();

          // Actualizar las opciones del select
          this.updateSelectOptions(fieldsToUpdate, formattedDocumentTypes);
        });
    }
  }

  getRequestClass(): void {
    this.listasService
      .getRequestClassList()
      .subscribe((requestClasses: ClaseSolicitudDto[]) => {
        const formattedRequestClasses = requestClasses.map(requestClass => ({
          Id: requestClass.Id,
          Nombre: requestClass.Nombre,
        }));
        const fieldsToUpdate = this.fields
          .filter(field => field.name.startsWith('claseOfSolicitation'))
          .map(field => field.name);
        this.updateSelectOptions(fieldsToUpdate, formattedRequestClasses);
      });
  }

  getAsuntoTramite(): void {
    this.listasService
      .getAsuntoTramiteList()
      .subscribe((asuntoTramite: AsuntoTramiteDto[]) => {
        const formattedAsuntoTramite = asuntoTramite.map(asuntoTramite => ({
          Id: asuntoTramite.Id,
          Nombre: asuntoTramite.Nombre,
        }));
        const fieldsToUpdate = this.fields
          .filter(field => field.name.startsWith('asuntoField'))
          .map(field => field.name);
        this.updateSelectOptions(fieldsToUpdate, formattedAsuntoTramite);
      });
  }

  updateSelectOptions(
    fieldNames: string | string[],
    options: { Id: string | number; Nombre?: string; TipoDocumento?: string }[]
  ): void {
    const fields = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    fields.forEach(fieldName => {
      const fieldToUpdate = this.fields.find(field => field.name === fieldName);

      if (fieldToUpdate) {
        fieldToUpdate.options = options.map(option => ({
          Id: option.Id,
          Nombre: option.Nombre ?? option.TipoDocumento ?? '',
        }));
      } else {
        console.warn(`Campo con el nombre ${fieldName} no encontrado.`);
      }
    });
  }

  onSubmit() {
    this.submitAction.emit(this.form);
    this.updateFieldVisibility();
  }

  onChange(event?: Event | string, value?: unknown) {

    this.changeAction.emit(this.form);
    this.updateFieldVisibility();
    if (typeof event === 'string' && event === this.dependsOnWithValueField) {
      setTimeout(() => {
        this.fields.filter(field =>
          field.value !== undefined && field.dependsOn === this.dependsOnWithValueField).forEach(field => {
            this.form.get(field.name)?.setValue(field.value)
          })
      }, 100);
    }
    const target =
      event instanceof Event
        ? (event?.target as HTMLInputElement).attributes.getNamedItem(
          'ng-reflect-name'
        )?.value
        : event;
    const valueControl =
      event instanceof Event
        ? ((event?.target as HTMLInputElement).attributes.getNamedItem(
          'ng-reflect-value'
        )?.value ?? (event?.target as HTMLInputElement).value)
        : value;
    this.changedControl.emit({
      controlName: target ? target?.toString() : event?.toString(),
      controlValue: valueControl || value,
      form: this.form,
    });
  }

  /* 🧰 metodo modal */

  closeModal(modalId: string) {
    const modalElement = document.getElementById(modalId);

    if (modalElement) {
      const bootstrapModal = (window as any).bootstrap;

      if (bootstrapModal) {
        // Obtener la instancia del modal
        const modalInstance = bootstrapModal.Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.hide(); // Cerrar el modal
        }
      }
    }
  }

  generateModalId(title: string | undefined): string {
    if (!title) {
      return 'defaultModalId';
    }

    return (
      'modalAdicional-' +
      title.toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '')
    );
  }

  edadMinimaValidator(aniosRequeridos: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const fechaNacimiento = control.value;
      if (!fechaNacimiento) {
        return null; // Si no hay valor, no se valida
      }

      const fechaActual = new Date();
      const diferenciaEnMilisegundos =
        fechaActual.getTime() - new Date(fechaNacimiento).getTime();
      const milisegundosPorAño = 1000 * 60 * 60 * 24 * 365.25;
      const diferenciaEnAnios = diferenciaEnMilisegundos / milisegundosPorAño;

      return diferenciaEnAnios < aniosRequeridos
        ? { edadRequerida: true }
        : null;
    };
  }

  togglePasswordVisibility(fieldName: string): void {
    this.showPassword[fieldName] = !this.showPassword[fieldName];
  }

  getFormattedMaxDate(fieldName: string, max: boolean | undefined): string | null {
    const field = this.form.get(fieldName);
    if (field && max) {
      return new Date(this.maxDate).toISOString().split('T')[0]; // Formato 'yyyy-MM-dd'
    }
    return null;
  }

  getFormattedMaxDateTime(fieldName: string, max: boolean | undefined): string | null {
    const field = this.form.get(fieldName);
    if (field && max) {
      const date = new Date(this.maxDate);

      // Convierte la fecha a formato 'yyyy-MM-ddTHH:mm'
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    return null;
  }

  onFileChange(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Previsualización (Base64)
    const reader = new FileReader();
    reader.onload = () => {
      this.previewImages[controlName] = reader.result as string; // para mostrar en la UI
      // Guardar el File real en el form
      this.form.get(controlName)?.setValue(file);
    };
    reader.readAsDataURL(file);


  }

  removeImage(controlName: string) {
    // quitar tanto el File como la previsualización
    this.form.get(controlName)?.setValue(null);
    this.previewImages[controlName] = null;
    this.changeAction.emit(this.form);
  }

  // Reset all preview images
  resetPreviewImages() {
    Object.keys(this.previewImages).forEach(key => {
      this.previewImages[key] = null;
      this.form.get(key)?.setValue(null); // opcional, limpia también el form
      this.changeAction.emit(this.form);
    });
  }


  // Select disabled
  obtenerNombreOpcion(field: FormField): string {
    const controlValue = this.form.get(field.name)?.value;

    // Como este caso es para selección única, asumimos que controlValue es un valor simple
    return field.options?.find(option => option.Id === controlValue)?.Nombre || '';
  }

  validateDecimalInput(event: any, maxlength: string): void {
    let inputValue = event.target.value;

    // Permite solo números y punto decimal
    inputValue = inputValue.replace(/[^0-9.]/g, '');

    // Limita el total de caracteres a maxlength
    if (inputValue.length > +maxlength) {
      inputValue = inputValue.substring(0, +maxlength);
    }

    // Solo un punto decimal
    const decimalCount = (inputValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      inputValue = inputValue.substring(0, inputValue.lastIndexOf('.'));
    }

    // Separa parte entera y decimal
    let [integerPart, decimalPart] = inputValue.split('.');

    // Formatea la parte entera con separador de miles
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 2 dígitos después del "."
    if (decimalPart !== undefined) {
      decimalPart = decimalPart.substring(0, 2);
    }

    // Reconstruye el número formateado
    const formattedValue = decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    event.target.value = formattedValue;
    this.form.get(event.target.name)?.setValue(formattedValue);
  }

  marcarTodoComoTocado(): void {
    this.form.markAllAsTouched();
  }
}
